import { Category, Account } from '@/types';
import { toast } from 'sonner';

interface AppState {
  categories: Category[];
  expanded: string[];
  accounts: Account[];
}

// Simple encryption key - in a real app, this would be in an env variable
const ENCRYPTION_KEY = 'BeeMapProjectKey2024';

const encryptData = (data: string): string => {
  // First convert the string to UTF-8 bytes, then to base64
  const utf8Bytes = new TextEncoder().encode(data);
  const result = [];
  for (let i = 0; i < utf8Bytes.length; i++) {
    const byte = utf8Bytes[i] ^ ENCRYPTION_KEY.charCodeAt(i % ENCRYPTION_KEY.length);
    result.push(String.fromCharCode(byte));
  }
  // Use btoa on ASCII-safe string
  return btoa(result.join(''));
};

const decryptData = (encryptedData: string): string => {
  try {
    const data = atob(encryptedData);
    const bytes = new Uint8Array(data.length);
    for (let i = 0; i < data.length; i++) {
      bytes[i] = data.charCodeAt(i) ^ ENCRYPTION_KEY.charCodeAt(i % ENCRYPTION_KEY.length);
    }
    // Convert bytes back to UTF-8 string
    return new TextDecoder().decode(bytes);
  } catch (error) {
    console.error('Decryption failed:', error);
    throw new Error('Invalid file format');
  }
};

export const downloadState = (state: AppState, projectName: string) => {
  const now = new Date();
  const formattedDate = now.toLocaleString('en-GB', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  }).replace(/[/]/g, '-').replace(',', '');
  
  const filename = `BeeMap ${projectName} ${formattedDate}.glmt`;
  console.log('Downloading state with filename:', filename);

  // Encrypt the state data
  const jsonData = JSON.stringify(state, null, 2);
  const encryptedData = encryptData(jsonData);
  
  const blob = new Blob([encryptedData], { type: 'application/octet-stream' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
  toast.success('State file downloaded successfully');
};

export const handleStateFileUpload = async (
  file: File,
  callbacks: {
    setCategories: (categories: Category[]) => void;
    setAccounts: (accounts: Account[]) => void;
    toggleCategory: (categoryId: string) => void;
    expanded: Set<string>;
  }
): Promise<void> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = (e) => {
      try {
        if (!e.target?.result) {
          throw new Error('Failed to read file');
        }

        // Decrypt the file content
        const decryptedData = decryptData(e.target.result as string);
        let state: AppState;
        
        try {
          state = JSON.parse(decryptedData);
        } catch (error) {
          console.error('Error parsing file:', error);
          toast.error('Invalid file format');
          reject(error);
          return;
        }

        console.log('Attempting to restore state:', state);

        // Validate state structure
        if (!state || typeof state !== 'object') {
          throw new Error('Invalid state file format');
        }

        // Handle categories
        if (Array.isArray(state.categories)) {
          console.log('Setting categories:', state.categories);
          callbacks.setCategories(state.categories);
        } else {
          console.warn('No valid categories found in state file');
        }

        // Handle accounts
        if (Array.isArray(state.accounts)) {
          console.log('Setting accounts:', state.accounts);
          callbacks.setAccounts(state.accounts);
        } else {
          console.warn('No valid accounts found in state file');
        }

        // Handle expanded state
        if (Array.isArray(state.expanded)) {
          console.log('Restoring expanded state:', state.expanded);
          state.expanded.forEach((id: string) => {
            if (!callbacks.expanded.has(id)) {
              callbacks.toggleCategory(id);
            }
          });
        }

        toast.success('State restored successfully');
        resolve();
      } catch (error) {
        console.error('Error processing state file:', error);
        toast.error('Error processing state file');
        reject(error);
      }
    };

    reader.onerror = () => {
      toast.error('Error reading state file');
      reject(new Error('FileReader error'));
    };

    reader.readAsText(file);
  });
};